/* eslint-disable import/no-extraneous-dependencies */
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import GetUserList from '../api/backend/GetKaiinChatAvailableChatUserListApi';
import GetStaffList, { Staff } from '../api/backend/GetCompanyStaffListApi';
import CreateAccount from '../api/backend/CreateCompanyStaffAccountApi';
import SearchKaiin from '../api/backend/SearchKaiinApi';
import { CognitoIdentityProvider } from '@aws-sdk/client-cognito-identity-provider';
import GetCompanyData from '../api/backend/GetCompanyDataApi';
import SearchKaiinList from '../api/backend/SearchKaiinListApi';
import SearchUserLogo from '../api/backend/SearchUserLogoApi';
import UserDataApi, { UserData } from '../api/backend/UserDataApi';
import UpdateAccountState, {
  AccountState
} from '../api/backend/UpdateUserStateApi';
import UpdateProfile, { Profile } from '../api/backend/UpdateStaffSettingApi';
import DeleteStaff from '../api/backend/DeleteStaffApi';
import DeleteStaffImg from '../api/backend/DeleteStaffImgApi';
import DeleteCompanyImg from '../api/backend/DeleteCompanyImgApi';
import UpdateDisplayLineInquiryButtonApi from '../api/backend/UpdateDisplayLineInquiryButtonApi';
import UpdateCompanyProfile, {
  CompanyProfile
} from '../api/backend/UpdateCompanyProfileApi';
import merge from 'lodash/merge';
import UpdateCompanyAccountStatus from '../api/backend/UpdateCompanyAccountStatusApi';
import Message, { MessageDetails } from '../api/backend/MessageApi';
import News from '../api/backend/NewsApi';
import IssueTokenApi from '../api/backend/IssueTokenApi';
import GetKaiinMessagingInfo from '../api/backend/GetKaiinMessagingInfoApi';
import GetInquiryList from '../api/backend/GetInquiryListApi';
import GetEnableDeviceTime from '../api/backend/GetEnableDeviceTimeApi';
import { CognitoCredentialProvider } from '../api/CognitoCredentialProvider';
import { AccountStatus } from '../constants';
import GetListChannelsApi from '../api/backend/GetListChannelsApi';
import SendNoticeTestMailApi from '../api/backend/SendNoticeTestMailApi';
import UpdatePushTokenApi, {
  PushTokenData
} from '../api/backend/UpdatePushTokenApi';
import GetUserMatchListApi from '../api/backend/GetUserMatchListApi';
import UpdateChannelVisibleApi from '../api/backend/UpdateChannelVisibleApi';
import UserMatchApi, {
  UpdateLastReadTimeParam
} from '../api/backend/UpdateUserMatchApi';

/**
 * @class IdentityService
 */
export class IdentityService {
  cognito = new CognitoIdentityProvider({
    region: process.env.NEXT_PUBLIC_AWS_REGION
  });
  isAdmin: boolean;

  _credentialProvider: CognitoCredentialProvider;
  bucketName: string;

  constructor(
    isAdmin: boolean,
    credentialProvider: CognitoCredentialProvider,
    bucketName: string = ''
  ) {
    this.isAdmin = isAdmin;
    this._credentialProvider = credentialProvider;
    this.bucketName = bucketName;
  }

  getAvailableUsers(search = '') {
    return new GetUserList(
      this._credentialProvider.getToken()
    ).getAvailableUsers(search);
  }

  getStaffList(): Promise<Staff[]> {
    return new GetStaffList(this._credentialProvider.getToken()).get();
  }

  getCompanyData() {
    return new GetCompanyData(this._credentialProvider.getToken()).get();
  }

  getCompanyMessagingInfo(companyId: string) {
    return new GetKaiinMessagingInfo(this._credentialProvider.getToken()).get(
      companyId
    );
  }

  getEnableDeviceTime(clientTime: string) {
    return new GetEnableDeviceTime(this._credentialProvider.getToken()).get(
      clientTime
    );
  }

  createAccount(params: { name?: string | undefined; email: string }) {
    return new CreateAccount(this._credentialProvider.getToken()).create(
      params
    );
  }

  deleteAccount(accountId: string) {
    return new DeleteStaff(this._credentialProvider.getToken()).delete({
      accountId,
      bucketName: this.bucketName
    });
  }

  deleteStaffImg() {
    return new DeleteStaffImg(this._credentialProvider.getToken()).delete({
      bucketName: this.bucketName
    });
  }

  deleteCompanyImg() {
    return new DeleteCompanyImg(this._credentialProvider.getToken()).delete({
      bucketName: this.bucketName
    });
  }

  updateAccountState(params: AccountState) {
    return new UpdateAccountState(this._credentialProvider.getToken()).update(
      params
    );
  }

  updateCompanyProfile(params: CompanyProfile) {
    return new UpdateCompanyProfile(this._credentialProvider.getToken()).send(
      merge(params, { body: { bucketName: this.bucketName } })
    );
  }

  sendTestMail(mail: string[]) {
    return new SendNoticeTestMailApi(this._credentialProvider.getToken()).send(
      mail
    );
  }

  searchKaiin(id: string) {
    return new SearchKaiin(this._credentialProvider.getToken()).get(id);
  }

  searchKaiinList(id: string[]) {
    return new SearchKaiinList(this._credentialProvider.getToken()).get(id);
  }

  searchUserLogo(id: string[]) {
    return new SearchUserLogo(this._credentialProvider.getToken()).get(id);
  }

  updateUserData(params: Partial<UserData>) {
    return new UserDataApi(this._credentialProvider.getToken()).set(params);
  }
  getUserData() {
    return new UserDataApi(this._credentialProvider.getToken()).get();
  }
  setNewsAsRead(id: string) {
    return new News(this._credentialProvider.getToken()).setRead(id);
  }
  getNews() {
    return new News(this._credentialProvider.getToken()).get();
  }
  updateProfile(params: Profile) {
    return new UpdateProfile(this._credentialProvider.getToken()).send(
      merge(params, { body: { bucketName: this.bucketName } })
    );
  }
  editMessage(
    params: MessageDetails & {
      content: string;
    }
  ) {
    return new Message(this._credentialProvider.getToken()).put(params);
  }

  deleteMessage(params: MessageDetails) {
    return new Message(this._credentialProvider.getToken()).delete(params);
  }

  issueToken() {
    return new IssueTokenApi(this._credentialProvider.getToken()).get();
  }

  getInquiryList(
    params:
      | {
          userId: string;
        }
      | {
          companyId: string;
        }
  ) {
    return new GetInquiryList(this._credentialProvider.getToken()).get(params);
  }

  changePassword(params: { previousPassword: string; newPassword: string }) {
    return this.cognito
      .changePassword({
        AccessToken: this._credentialProvider.getAccessToken(),
        PreviousPassword: params.previousPassword,
        ProposedPassword: params.newPassword
      })
      .catch((error) => error);
  }
  updateDisplayLineInquiryButton(params: {
    displayLineInquiryButtonFLG: boolean;
  }) {
    return new UpdateDisplayLineInquiryButtonApi(
      this._credentialProvider.getToken()
    ).update(params);
  }
  setCompanyState(accountStatus: AccountStatus) {
    return new UpdateCompanyAccountStatus(
      this._credentialProvider.getToken()
    ).update(accountStatus);
  }

  getListChannels() {
    return new GetListChannelsApi(this._credentialProvider.getToken()).get();
  }

  getToken() {
    return this._credentialProvider.getToken();
  }

  updatePushToken(params: PushTokenData) {
    return new UpdatePushTokenApi(this._credentialProvider.getToken()).set(
      params
    );
  }

  getUserMatchList(companyId: string) {
    return new GetUserMatchListApi(this._credentialProvider.getToken()).get(
      companyId
    );
  }

  updateChannelVisible(
    companyId: string,
    matchId: string,
    isHideChannel: boolean
  ) {
    return new UpdateChannelVisibleApi(
      this._credentialProvider.getToken()
    ).post(companyId, matchId, isHideChannel);
  }

  updateLastReadTimestamp(params: UpdateLastReadTimeParam) {
    return new UserMatchApi(
      this._credentialProvider.getToken()
    ).updateLastReadTimestamp(params);
  }

  getLastReadTimestamp(params: UpdateLastReadTimeParam) {
    return new UserMatchApi(
      this._credentialProvider.getToken()
    ).getLastReadTimestamp(params);
  }
}

export default IdentityService;
